import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'

import { api } from '../api.js'
import { date } from '../format.js'

/**
 * Where Paystack sends the browser back.
 *
 * ⚠️ Arriving here does NOT mean the payment is recorded. The redirect is a
 * hint; the webhook is the trigger; our own record is the truth. The webhook
 * may land before the browser does, after it, or not at all — in which case
 * the reconciliation sweep picks it up within the hour.
 *
 * So this screen polls our own subscription state and says exactly what it
 * knows. Claiming success on the redirect would tell a shopkeeper they can
 * sell when they cannot; claiming failure would send them to pay twice.
 */
const POLL_INTERVAL_MS = 2000
const POLL_ATTEMPTS = 10

type Outcome =
  | { state: 'confirming'; attempt: number }
  | { state: 'confirmed'; paidThrough: string | null }
  | { state: 'still-waiting' }

export function PaymentReturn() {
  const [outcome, setOutcome] = useState<Outcome>({ state: 'confirming', attempt: 0 })
  const cancelled = useRef(false)

  useEffect(() => {
    cancelled.current = false

    async function poll() {
      for (let attempt = 1; attempt <= POLL_ATTEMPTS; attempt++) {
        if (cancelled.current) return

        const subscription = await api.subscription().catch(() => null)
        if (cancelled.current) return

        if (subscription?.status === 'active') {
          setOutcome({ state: 'confirmed', paidThrough: subscription.paidThrough })
          return
        }

        setOutcome({ state: 'confirming', attempt })
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS))
      }

      if (!cancelled.current) setOutcome({ state: 'still-waiting' })
    }

    void poll()
    return () => {
      cancelled.current = true
    }
  }, [])

  return (
    <main className="narrow">
      {outcome.state === 'confirming' ? (
        <>
          <h1>Confirming your payment…</h1>
          <p className="muted">
            This usually takes a few seconds. You do not need to pay again.
          </p>
        </>
      ) : null}

      {outcome.state === 'confirmed' ? (
        <>
          <h1>Payment received</h1>
          <p>
            Your subscription is active through <strong>{date(outcome.paidThrough)}</strong>.
          </p>
          <p><Link to="/">Back to your account</Link></p>
        </>
      ) : null}

      {outcome.state === 'still-waiting' ? (
        <>
          <h1>Still confirming</h1>
          <p>
            We have not had confirmation from the payment provider yet. This happens
            occasionally and usually resolves within the hour on its own.
          </p>
          <p><strong>Do not pay again.</strong> If the money left your account, it will be
            matched automatically — we check for payments we have not heard about.
          </p>
          <p><Link to="/">Back to your account</Link></p>
        </>
      ) : null}
    </main>
  )
}
